import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Checkbox, Dialog, DialogContent, DialogTitle, IconButton, useTheme } from '@material-ui/core'
import { Add, Close, InsertDriveFileOutlined } from '@material-ui/icons'
import TLServiceComponent from '../../../../../contexts/TLServiceComponent'

const MIN_WORDS_TO_TRIGGER = 4
const DEBOUNCE_MS = 2000
const SEARCH_LIMIT = 5
const MIN_SIMILARITY = 0.8

const SOURCE_TYPES = ['archive', 'external']

const countWords = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length

const getFileName = (item) => item.archive_name || item.file_name || ''

const sortBySimilarity = (chunks) => chunks.slice().sort((a, b) => b.similarity - a.similarity)

// Chunks are kept as a list in document order: each one becomes its own <context id="...">
// block in the request, matching the id and per-chunk tag the backend uses for its own RAG
// context (worker.cpp), so the model sees separate, addressable fragments.
const buildFragments = (archiveId, chunks) => chunks.slice()
  .sort((a, b) => (a.chunk_index ?? 0) - (b.chunk_index ?? 0))
  .filter(chunk => chunk.plain_text)
  .map(chunk => ({ id: `${archiveId}_${chunk.chunk_index ?? 0}`, text: chunk.plain_text }))

// Chunks are re-sorted for display and for the request, so every chunk gets a key that
// stays the same in both orders and is used to track the selection.
const groupTopFile = (items) => {
  const byArchive = {}
  items.forEach(item => {
    if (!byArchive[item.archive_id]) {
      byArchive[item.archive_id] = { archiveId: item.archive_id, fileName: getFileName(item), chunks: [] }
    }
    const group = byArchive[item.archive_id]
    group.chunks.push({ ...item, chunkKey: item.chunk_index ?? `pos-${group.chunks.length}` })
  })
  const files = Object.values(byArchive).map(file => ({
    ...file,
    topSimilarity: Math.max(...file.chunks.map(chunk => chunk.similarity))
  }))
  files.sort((a, b) => b.topSimilarity - a.topSimilarity)
  return files[0] || null
}

// Everything is selected until the user unchecks something, so an empty list means "all chunks".
const getSelectedChunks = (file, deselectedChunks) => {
  const deselected = deselectedChunks[file.archiveId] || []
  return file.chunks.filter(chunk => deselected.indexOf(chunk.chunkKey) === -1)
}

const RagRecommendations = ({ mainEditorText, contextFragments, setContextFragments }) => {
  const { t } = useTranslation()
  const { theme: { colors, bgColors, borderColors } } = useTheme()
  const tlService = useContext(TLServiceComponent)
  const token = useSelector(state => state.userData.token)
  const enabledSections = useSelector(state => state.userData.user?.enabled_frontend_sections || [])
  const ragIndexEnabled = enabledSections.indexOf('rag_index') > -1

  const [suggestions, setSuggestions] = useState({ archive: null, external: null })
  const [openFile, setOpenFile] = useState(null)
  const [deselectedChunks, setDeselectedChunks] = useState({})
  const requestIdRef = useRef(0)
  const isFetchingRef = useRef(false)
  const pendingRef = useRef(null)

  // Ensures at most one archive+external search cycle is ever in flight: if the debounce
  // timer fires while a previous cycle is still running, the newest query is queued and
  // picked up as soon as that cycle finishes, instead of firing a second cycle in parallel.
  const runSearch = useCallback((query, requestId) => {
    isFetchingRef.current = true
    Promise.all(SOURCE_TYPES.map(type => tlService.searchRAGArchives(token, query, type, SEARCH_LIMIT)
      // plain_text comes back empty when the backend cannot re-slice the chunk out of the
      // current archive text; such a chunk adds nothing to the request, so it is dropped here.
      .then(res => (res?.data || []).filter(item => item.similarity >= MIN_SIMILARITY && item.plain_text))
      .catch(() => [])
    )).then(([archiveItems, externalItems]) => {
      if (requestId !== requestIdRef.current) return
      setSuggestions({ archive: groupTopFile(archiveItems), external: groupTopFile(externalItems) })
    }).finally(() => {
      isFetchingRef.current = false
      if (pendingRef.current) {
        const next = pendingRef.current
        pendingRef.current = null
        runSearch(next.query, next.requestId)
      }
    })
  }, [tlService, token])

  useEffect(() => {
    if (!ragIndexEnabled || countWords(mainEditorText) < MIN_WORDS_TO_TRIGGER) {
      pendingRef.current = null
      setSuggestions({ archive: null, external: null })
      return undefined
    }

    // Every change (re)starts the timeout from scratch (the cleanup below cancels
    // whatever was pending). Only once the text has been quiet for DEBOUNCE_MS does
    // the timeout actually fire, and it always reads the current mainEditorText at
    // that moment via the closure, so it always sends the latest version of the text.
    const timeoutId = setTimeout(() => {
      const query = mainEditorText.trim()
      const requestId = ++requestIdRef.current
      if (isFetchingRef.current) {
        pendingRef.current = { query, requestId }
      } else {
        runSearch(query, requestId)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [mainEditorText, runSearch, ragIndexEnabled])

  const removeFragment = (archiveId) => {
    setContextFragments(prev => {
      const next = { ...prev }
      delete next[archiveId]
      return next
    })
  }

  const setFragment = (archiveId, chunks) => {
    const fragments = buildFragments(archiveId, chunks)
    if (!fragments.length) {
      removeFragment(archiveId)
      return
    }
    setContextFragments(prev => ({ ...prev, [archiveId]: fragments }))
  }

  const handleToggleAddToRequest = (file) => {
    if (contextFragments[file.archiveId] !== undefined) {
      removeFragment(file.archiveId)
    } else {
      setFragment(file.archiveId, getSelectedChunks(file, deselectedChunks))
    }
  }

  const handleToggleChunk = (file, chunkKey) => {
    const deselected = deselectedChunks[file.archiveId] || []
    const nextDeselected = deselected.indexOf(chunkKey) > -1
      ? deselected.filter(key => key !== chunkKey)
      : [...deselected, chunkKey]
    const nextDeselectedChunks = { ...deselectedChunks, [file.archiveId]: nextDeselected }
    setDeselectedChunks(nextDeselectedChunks)
    // A file already in the request follows the selection: unchecking its last chunk drops it.
    if (contextFragments[file.archiveId] !== undefined) {
      setFragment(file.archiveId, getSelectedChunks(file, nextDeselectedChunks))
    }
  }

  const cards = SOURCE_TYPES
    .map(type => ({ type, file: suggestions[type] }))
    .filter(({ file }) => !!file)

  if (!ragIndexEnabled || !cards.length) {
    return null
  }

  return (
    <div className="mt-4 p-3" style={{
      backgroundColor: bgColors.bgPrimary0,
      borderRadius: '10px',
      border: `1px solid ${borderColors.borderPrimary200}`,
      boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)'
    }}>
      <p className="small mb-3" style={{ color: colors.colorMutedText }}>
        {t('ai_gen_rag_recommendation_text')}
      </p>
      {cards.map(({ type, file }) => {
        const selectedChunks = getSelectedChunks(file, deselectedChunks)
        const isAdded = contextFragments[file.archiveId] !== undefined
        return (
        <div key={type} className="d-flex align-items-center justify-content-between mb-2 p-2" style={{
          border: `1px solid ${borderColors.borderPrimary200}`,
          borderRadius: '8px'
        }}>
          <div className="d-flex flex-column" style={{ minWidth: 0, gap: '4px' }}>
            <div className="d-flex align-items-center" style={{ minWidth: 0, gap: '0.5rem' }}>
              <InsertDriveFileOutlined style={{ color: colors.colorMutedText, flexShrink: 0 }} fontSize="small"/>
              <span
                onClick={() => setOpenFile({ type, ...file })}
                title={file.fileName}
                style={{
                  color: colors.colorBlue1000,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {file.fileName}
              </span>
            </div>
            <div className="d-flex align-items-center" style={{ gap: '0.5rem', paddingLeft: '24px' }}>
              <span style={{
                backgroundColor: type === 'archive' ? bgColors.bgBlue150 : bgColors.bgLightYellow,
                color: colors.colorText,
                borderRadius: '999px',
                padding: '2px 10px',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {t(type === 'archive' ? 'ai_gen_rag_tag_archive' : 'ai_gen_rag_tag_external')}
              </span>
              <span style={{ color: colors.colorMutedText, fontSize: '0.75rem', flexShrink: 0 }}>
                {Math.round(file.topSimilarity * 100)}%
              </span>
              {selectedChunks.length < file.chunks.length && (
                <span style={{ color: colors.colorMutedText, fontSize: '0.75rem', flexShrink: 0 }}>
                  {selectedChunks.length}/{file.chunks.length}
                </span>
              )}
            </div>
          </div>
          <IconButton
            size="small"
            disabled={!selectedChunks.length}
            title={t(isAdded ? 'ai_gen_rag_remove_from_request' : 'ai_gen_rag_add_to_request')}
            style={{
              backgroundColor: isAdded ? bgColors.bgPrimary300 : bgColors.bgProgress100,
              color: colors.colorBtnText,
              width: 28,
              height: 28,
              flexShrink: 0,
              marginLeft: '0.5rem',
              opacity: selectedChunks.length ? 1 : 0.5
            }}
            onClick={() => handleToggleAddToRequest(file)}
          >
            <Add fontSize="small"/>
          </IconButton>
        </div>
        )
      })}

      <Dialog open={!!openFile} onClose={() => setOpenFile(null)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 0 }}>
          <span style={{
            color: colors.colorMutedText,
            fontSize: '0.7rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {openFile?.fileName}
          </span>
          <IconButton size="small" onClick={() => setOpenFile(null)}>
            <Close/>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p style={{ color: colors.colorText, fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            {t('ai_gen_rag_modal_headline')}
          </p>
          {openFile?.chunks && sortBySimilarity(openFile.chunks).map((chunk, index) => {
            const isSelected = (deselectedChunks[openFile.archiveId] || []).indexOf(chunk.chunkKey) === -1
            return (
              <div key={chunk.chunkKey} className="mb-3 pb-3 d-flex" style={{
                gap: '0.5rem',
                borderBottom: index < openFile.chunks.length - 1 ? `1px solid ${borderColors.borderPrimary200}` : 'none'
              }}>
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={() => handleToggleChunk(openFile, chunk.chunkKey)}
                  style={{ padding: 0, alignSelf: 'flex-start', color: colors.colorBlue1000 }}
                />
                <div style={{ minWidth: 0, opacity: isSelected ? 1 : 0.5 }}>
                  <div className="mb-1" style={{ color: colors.colorMutedText, fontSize: '0.75rem' }}>
                    {Math.round(chunk.similarity * 100)}%
                  </div>
                  <div style={{ color: colors.colorText, whiteSpace: 'pre-wrap' }}>
                    {chunk.plain_text}
                  </div>
                </div>
              </div>
            )
          })}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RagRecommendations
